import {
  Image,
  Pressable,
  Text,
  View,
  type ImageSourcePropType,
  type StyleProp,
  type ViewStyle,
} from "react-native";
import type { ReactNode } from "react";
import {
  BookOpen,
  FlaskConical,
  Mars,
  Snowflake,
  Venus,
  VenusAndMars,
  type LucideIcon,
} from "lucide-react-native";
import { Tag, TextActionButton, type TagTone } from "@louise/moon-ui";
import type { SupplementBody, TestBody } from "@src/packages/content";
import { useTranslations } from "@src/packages/i18n";
import { EMBER } from "../../onboarding/theme";

import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { semanticColors, withAlpha } from "@louise/theme/tokens";

type EvidenceStrength = NonNullable<TestBody["evidenceStrength"]>;
type FactSheetAudience = SupplementBody["forWhom"];

/**
 * One tag under a fact sheet's title, named by what it says so every sheet
 * draws the same meaning the same way.
 */
export type FactSheetTagItem =
  /**
   * What kind of thing it is (Genetic, Supplement, a drug class), how a
   * medication is taken (Subcutaneous), or what a kit offers (FSA/HSA
   * eligible).
   */
  | { kind: "category" | "route" | "feature"; label: string; icon?: LucideIcon }
  | { kind: "refrigerated"; label: string }
  | { kind: "evidence"; label: string; strength: EvidenceStrength }
  /** Who it is for, as the symbol alone. */
  | { kind: "audience"; audience: FactSheetAudience };

/** A way out to the related sheets, shown under the tags. */
export type FactSheetLink = {
  label: string;
  /** Stable analytics identifier, since the label is translated. */
  action: string;
  onPress: () => void;
};

const EVIDENCE_TONE: Record<EvidenceStrength, TagTone> = {
  strong: "green",
  moderate: "gold",
  emerging: "gold",
  mixed: "gold",
  debunked: "coral",
};

const AUDIENCE_ICON: Record<FactSheetAudience, LucideIcon> = {
  her: Venus,
  him: Mars,
  both: VenusAndMars,
};

const AUDIENCE_LABEL_KEY: Record<FactSheetAudience, string> = {
  her: "learn.fact_sheet_audience.for_her",
  him: "learn.fact_sheet_audience.for_him",
  both: "learn.fact_sheet_audience.for_both_partners",
};

export function FactSheetTag({ tag }: { tag: FactSheetTagItem }) {
  const t = useTranslations();
  switch (tag.kind) {
    case "refrigerated":
      return <Tag label={tag.label} tone="blue" icon={Snowflake} />;
    case "evidence":
      return (
        <Tag
          label={tag.label}
          tone={EVIDENCE_TONE[tag.strength]}
          icon={FlaskConical}
        />
      );
    case "audience": {
      const label = t(AUDIENCE_LABEL_KEY[tag.audience]);
      return (
        <Tag
          label={label}
          accessibilityLabel={label}
          variant="outline"
          tone="neutral"
          icon={AUDIENCE_ICON[tag.audience]}
          iconOnly
        />
      );
    }
    default:
      return (
        <Tag
          label={tag.label}
          variant="outline"
          tone="neutral"
          icon={tag.icon}
        />
      );
  }
}

/** A fact sheet's tags, centered, with any related-sheet links under them. */
export function FactSheetTagRow({
  tags,
  links = [],
  style,
}: {
  tags: FactSheetTagItem[];
  links?: FactSheetLink[];
  style?: StyleProp<ViewStyle>;
}) {
  if (!tags.length && !links.length) return null;
  return (
    <View style={[{ alignItems: "center", gap: 6 }, style]}>
      {tags.length ? (
        <View
          style={{
            flexDirection: "row",
            flexWrap: "wrap",
            justifyContent: "center",
            gap: 6,
          }}
        >
          {tags.map((tag, index) => (
            <FactSheetTag key={`${tag.kind}-${index}`} tag={tag} />
          ))}
        </View>
      ) : null}
      {links.map((link) => (
        <TextActionButton
          key={link.label}
          label={link.label}
          action={link.action}
          onPress={link.onPress}
          icon={BookOpen}
          size="sm"
          centered
        />
      ))}
    </View>
  );
}

/**
 * The older hand-built label. The procedure, program and metric sheets still
 * use it, as do the connected-marker pills on a marker sheet; every other
 * sheet uses FactSheetTag.
 */
export type FactSheetHeaderBadge = {
  label?: string;
  icon?: LucideIcon;
  accent?: boolean;
  color?: string;
  accessibilityLabel?: string;
  onPress?: () => void;
};

type Props = {
  title: string;
  subtitle?: string;
  emoji?: string;
  imageSource?: ImageSourcePropType;
  imageSize?: number;
  visual?: ReactNode;
  tags?: FactSheetTagItem[];
  links?: FactSheetLink[];
  /** The program and metric sheets' older labels. */
  badges?: FactSheetHeaderBadge[];
};

export function FactSheetBadge({
  label,
  icon: Icon,
  accent,
  color,
  accessibilityLabel,
  onPress,
}: FactSheetHeaderBadge) {
  const capturePress = usePressCapture("FactSheetBadge");
  const textColor = accent
    ? semanticColors.accent.default
    : (color ?? EMBER.colors.textMid);
  const iconColor = accent
    ? semanticColors.accent.default
    : (color ?? EMBER.colors.textFaint);
  const content = (
    <>
      {Icon ? <Icon size={10} color={iconColor} strokeWidth={1.5} /> : null}
      {label ? (
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 11,
            fontWeight: "500",
            color: textColor,
            letterSpacing: 0,
            // Let long sentences wrap within the badge instead of pushing it
            // past the parent card.
            flexShrink: 1,
          }}
        >
          {label}
        </Text>
      ) : null}
    </>
  );
  const getStyle = (pressed = false) => ({
    flexDirection: "row" as const,
    alignItems: "center" as const,
    gap: Icon ? 5 : 4,
    paddingHorizontal: label ? 10 : 8,
    paddingVertical: 5,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: accent
      ? withAlpha(semanticColors.accent.default, 0.22)
      : semanticColors.border.subtle,
    backgroundColor: accent
      ? pressed
        ? withAlpha(semanticColors.accent.default, 0.13)
        : semanticColors.accent.tint
      : pressed
        ? semanticColors.overlay.pressed
        : semanticColors.surface.muted,
    // Allow the chip to shrink (and its Text to wrap) when the wrapping row
    // doesn't have enough horizontal room for the full label.
    flexShrink: 1,
    maxWidth: "100%" as const,
  });

  if (onPress) {
    return (
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={accessibilityLabel ?? label}
        onPress={() => {
          capturePress(pressLabel(label, accessibilityLabel), "fact_sheet_nav");
          onPress();
        }}
        style={({ pressed }) => getStyle(pressed)}
      >
        {content}
      </Pressable>
    );
  }

  return (
    <View
      accessibilityLabel={accessibilityLabel ?? label}
      style={getStyle(false)}
    >
      {content}
    </View>
  );
}

export default function FactSheetHeader({
  title,
  subtitle,
  emoji,
  imageSource,
  imageSize = 92,
  visual,
  tags = [],
  links = [],
  badges = [],
}: Props) {
  const hasVisual = Boolean(visual || imageSource || emoji);
  const hasTagRow = tags.length > 0 || links.length > 0;

  return (
    <>
      {hasVisual ? (
        <View
          style={{
            alignItems: "center",
            marginTop: 8,
            marginBottom: 14,
          }}
        >
          {visual ? (
            visual
          ) : imageSource ? (
            <Image
              source={imageSource}
              style={{ width: imageSize, height: imageSize }}
              resizeMode="contain"
            />
          ) : (
            <View
              style={{
                width: 76,
                height: 76,
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Text
                accessibilityRole="image"
                style={{ fontSize: 56, lineHeight: 64, textAlign: "center" }}
              >
                {emoji}
              </Text>
            </View>
          )}
        </View>
      ) : null}

      <Text
        className="font-dm-sans"
        style={{
          fontSize: 22,
          fontWeight: "700",
          color: EMBER.colors.text,
          textAlign: "center",
          lineHeight: 28,
          paddingHorizontal: 28,
          paddingTop: hasVisual ? 0 : 16,
        }}
      >
        {title}
      </Text>

      {subtitle ? (
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 14,
            color: EMBER.colors.textLight,
            lineHeight: 20,
            marginTop: 6,
            marginBottom: hasTagRow || badges.length ? 12 : 8,
            textAlign: "center",
          }}
        >
          {subtitle}
        </Text>
      ) : null}

      {hasTagRow ? (
        <FactSheetTagRow
          tags={tags}
          links={links}
          style={{ marginTop: subtitle ? 0 : 12 }}
        />
      ) : badges.length ? (
        <View
          style={{
            flexDirection: "row",
            flexWrap: "wrap",
            justifyContent: "center",
            gap: 6,
            marginTop: subtitle ? 0 : 12,
            marginBottom: 0,
          }}
        >
          {badges.map((badge, index) => (
            <FactSheetBadge
              key={badge.label ?? badge.accessibilityLabel ?? index}
              {...badge}
            />
          ))}
        </View>
      ) : null}
    </>
  );
}
